import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { formatCents, parseCents, sumCents } from '@shared/money';
import { todayIn } from '@shared/time';
import { createQuoteSchema } from '@shared/schemas/quote';
import type { CreateQuoteInput } from '@shared/schemas/quote';
import styles from './NewQuotePage.module.css';

/**
 * A service line the owner is building for this quote. The unit price is a
 * FROZEN COPY taken from the catalog at the moment it was picked -- changing
 * the catalog later must not alter this quote.
 *
 * `priceInput` holds the raw string the owner is typing (e.g. "45.00") so the
 * input stays controlled while they edit. `unitPriceCents` is the parsed
 * integer-cents value used for the total and the POST body.
 */
interface ServiceLine {
  description: string;
  quantity: number;
  unitPriceCents: number;
  priceInput: string;
  serviceId?: string;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

interface Property {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

interface ServiceCatalogItem {
  id: string;
  name: string;
  defaultPriceCents: number;
}

interface CreatedQuote {
  id: string;
}

/**
 * The org timezone is read from the session. The session module is mocked in
 * tests, so this reads whatever the mock provides.
 */
function useOrgTimezone(): string {
  const session = useSession();
  // The session user may carry an organization timezone. Fall back to UTC so
  // the component never crashes if the field is absent.
  const tz =
    (session.user as { organizationTimezone?: string } | null)?.organizationTimezone ?? 'UTC';
  return tz;
}

function customerLabel(c: Customer): string {
  const parts = [c.firstName, c.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : (c.companyName ?? c.email ?? c.id);
}

function propertyLabel(p: Property): string {
  const line2 = [p.city, p.state, p.postalCode].filter(Boolean).join(', ');
  return [p.addressLine1, line2].filter(Boolean).join(', ');
}

export function NewQuotePage() {
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();

  // --- Data loading ---
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);

  // --- Form state ---
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [lines, setLines] = useState<ServiceLine[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // A REF guards against double-submit. Two taps in one tick both run before
  // React re-renders, so a `submitting` boolean would let the second through.
  // The ref is flipped synchronously inside handleSubmit before any await, so
  // the second tap sees it and returns immediately.
  const submittingRef = useRef(false);

  // Default the date to the ORG's today, never the browser's date.
  useEffect(() => {
    setIssueDate(todayIn(orgTimezone));
  }, [orgTimezone]);

  // Load customers on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingCustomers(true);
    apiGet<Customer[]>('/api/v1/customers')
      .then((data) => {
        if (!cancelled) {
          setCustomers(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCustomers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCustomers(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the service catalog on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingServices(true);
    apiGet<ServiceCatalogItem[]>('/api/v1/services')
      .then((data) => {
        if (!cancelled) {
          setServices(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServices([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingServices(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the customer changes, refetch THAT customer's properties and clear
  // the chosen property. A quote whose property belongs to a different customer
  // is rejected by the server with a 400 -- do not let the owner build that
  // request in the first place.
  useEffect(() => {
    if (!customerId) {
      setProperties([]);
      setPropertyId('');
      return;
    }
    let cancelled = false;
    setLoadingProperties(true);
    apiGet<Property[]>(`/api/v1/properties?customerId=${encodeURIComponent(customerId)}`)
      .then((data) => {
        if (!cancelled) {
          setProperties(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProperties([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProperties(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // --- Line item helpers ---
  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { description: '', quantity: 1, unitPriceCents: 0, priceInput: '' },
    ]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (
    index: number,
    field: 'description' | 'quantity' | 'priceInput',
    value: string | number
  ) => {
    setLines((prev) => {
      const copy = [...prev];
      const original = copy[index];
      if (!original) return prev;
      const line: ServiceLine = {
        description: original.description,
        quantity: original.quantity,
        unitPriceCents: original.unitPriceCents,
        priceInput: original.priceInput,
        serviceId: original.serviceId,
      };
      if (field === 'quantity') {
        const n = Number(value);
        line.quantity = Number.isNaN(n) || n < 0 ? 0 : Math.floor(n);
      } else if (field === 'priceInput') {
        // unitPriceCents is INTEGER CENTS. Take dollars from the owner and
        // convert with parseCents. Never a float, never multiply by 100
        // yourself, never toFixed.
        line.priceInput = value as string;
        const parsed = parseCents(value as string);
        line.unitPriceCents = parsed === null ? 0 : parsed;
      } else {
        line.description = value as string;
      }
      copy[index] = line;
      return copy;
    });
  };

  const applyCatalogItem = (index: number, item: ServiceCatalogItem) => {
    setLines((prev) => {
      const copy = [...prev];
      const line: ServiceLine = {
        description: item.name,
        quantity: 1,
        unitPriceCents: item.defaultPriceCents,
        priceInput: (item.defaultPriceCents / 100).toFixed(2),
        serviceId: item.id,
      };
      copy[index] = line;
      return copy;
    });
  };

  // Running total computed from the line values the owner has entered.
  const estimateCents = useMemo(
    () => sumCents(lines.map((line) => line.quantity * line.unitPriceCents)),
    [lines]
  );

  // A line is invalid if its price is non-numeric or negative.
  const lineErrors = lines.map((line) => {
    if (line.unitPriceCents < 0) {
      return 'Price must not be negative';
    }
    return null;
  });
  const hasLineError = lineErrors.some((e) => e !== null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    // Flip the ref BEFORE any await so a second tap in the same tick is a
    // no-op. This is the exactly-once guard.
    submittingRef.current = true;
    setSubmitting(true);
    setServerError(null);

    // Field-level validation produces user-friendly messages before the
    // schema runs. The schema is still the authority on the wire shape.
    if (!customerId) {
      setServerError('Choose a customer');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    if (lines.length === 0) {
      setServerError('Add at least one service line');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    if (hasLineError) {
      setServerError('Fix the errors in your service lines');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    const body: CreateQuoteInput = {
      customerId,
      propertyId: propertyId || undefined,
      issueDate,
      expirationDate: expirationDate || undefined,
      customerMessage: customerMessage || undefined,
      internalNotes: internalNotes || undefined,
      items: lines.map((line) => ({
        serviceId: line.serviceId,
        description: line.description,
        quantity: line.quantity,
        unitPriceCents: line.unitPriceCents,
      })),
    };

    // Validate against the EXACT server contract before posting. A client
    // whose rules disagree with the server's rejects valid quotes or shows the
    // user a raw 400.
    const result = createQuoteSchema.safeParse(body);
    if (!result.success) {
      setServerError(result.error.errors[0]?.message ?? 'Invalid form');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const created = await apiPost<CreatedQuote>('/api/v1/quotes', body);
      // Only navigate after the server confirms.
      navigate(`/quotes/${created.id}`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
      // Release the guard so the user can retry after a failure.
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.main}>
      <h1 className={styles.title}>Create a quote</h1>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* --- Customer --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Customer</legend>
          <select
            className={styles.select}
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setPropertyId('');
            }}
            aria-label="Customer"
            disabled={loadingCustomers}
          >
            <option value="" disabled>
              {loadingCustomers ? 'Loading customers…' : 'Choose a customer'}
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {customerLabel(c)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Property --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Property</legend>
          <select
            className={styles.select}
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            aria-label="Property"
            disabled={loadingProperties || !customerId}
          >
            <option value="" disabled>
              {loadingProperties
                ? 'Loading properties…'
                : !customerId
                  ? 'Choose a customer first'
                  : 'Choose a property'}
            </option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {propertyLabel(p)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Dates --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Dates</legend>
          <input
            className={styles.input}
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            aria-label="Issue date"
          />
          <input
            className={styles.input}
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            placeholder="Optional expiration date"
            aria-label="Expiration date"
          />
        </fieldset>

        {/* --- Service lines --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Services</legend>
          {lines.map((line, index) => (
            <div key={index} className={styles.line}>
              <select
                className={styles.select}
                aria-label={`Catalog for line ${index + 1}`}
                onChange={(e) => {
                  const item = services.find((s) => s.id === e.target.value);
                  if (item) {
                    applyCatalogItem(index, item);
                  }
                }}
                value=""
                disabled={loadingServices}
              >
                <option value="" disabled>
                  {loadingServices ? 'Loading…' : 'Pick from catalog'}
                </option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {formatCents(s.defaultPriceCents)}
                  </option>
                ))}
              </select>
              <input
                className={styles.input}
                type="text"
                value={line.description}
                onChange={(e) => updateLine(index, 'description', e.target.value)}
                placeholder="Description"
                aria-label={`Description for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="number"
                min={0}
                value={line.quantity}
                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                aria-label={`Quantity for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="text"
                value={line.priceInput}
                onChange={(e) => updateLine(index, 'priceInput', e.target.value)}
                placeholder="Price"
                aria-label={`Unit price for line ${index + 1}`}
              />
              {lineErrors[index] ? (
                <span className={styles.error} role="alert">
                  {lineErrors[index]}
                </span>
              ) : null}
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeLine(index)}
                aria-label={`Remove line ${index + 1}`}
                disabled={lines.length <= 1}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.addBtn}
            onClick={addLine}
            aria-label="Add a service line"
          >
            Add service
          </button>
        </fieldset>

        {/*
          --- Notes ---
          Both values were already collected in state and already sent in the
          body; the inputs for them were simply never rendered, which the
          compiler noticed only as two unused setters. A quote the customer
          reads with no way to say anything on it is the kind of gap a green
          test suite is perfectly happy with.
        */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Notes</legend>
          <textarea
            className={styles.input}
            value={customerMessage}
            onChange={(e) => setCustomerMessage(e.target.value)}
            placeholder="Message to the customer (appears on the quote)"
            aria-label="Message to the customer"
            rows={3}
          />
          <textarea
            className={styles.input}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Internal notes (never shown to the customer)"
            aria-label="Internal notes"
            rows={3}
          />
        </fieldset>

        {/* --- Total --- */}
        <div className={styles.total}>
          <span>Estimated total:</span>
          <strong>{formatCents(estimateCents)}</strong>
        </div>

        {/* --- Server error --- */}
        {serverError ? (
          <div className={styles.serverError} role="alert">
            {serverError}
          </div>
        ) : null}

        {/* --- Submit --- */}
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Creating…' : 'Create quote'}
        </button>
        {lines.length === 0 && (
          <p className={styles.hint}>Add at least one service line to create the quote.</p>
        )}
      </form>
    </main>
  );
}
